<template>
    <!--
        The navbar strip: `h-(--ui-header-height)` with a bottom hairline, the page TITLE at the
        left and the controls at the right. The title is the current section's menu label
        (`navTitleKey`), so it is the same word the sidebar shows and no new copy is introduced.

        It paints `--ui-bg-chrome` — the same surface the sidebar takes, so THE FRAME IS ONE
        SURFACE: white over the neutral-100 canvas in light, and in dark the panel colour, the
        same mix the cards take, over the darker neutral-900 canvas. The hairline below it is
        the seam, not the separation.

        The user menu is NOT here any more — it lives at the bottom of the sidebar, where an
        admin console puts it. What stays is what belongs to the whole window rather than to the
        person: the light/dark toggle (vue-spec `theme.md`).
    -->
    <header class="h-(--ui-header-height) shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-default bg-(--ui-bg-chrome)">
        <div class="flex items-center gap-2 min-w-0">
            <!-- Opens the drawer. It does not have to close it: once open, the drawer covers
                 this corner, and the X inside it and the backdrop are the ways out. -->
            <UButton
                v-if="isMobile"
                color="neutral"
                variant="ghost"
                icon="i-lucide-menu"
                :aria-label="$t('layout.open_menu')"
                @click="openMenu"
            />
            <!-- The tenant being worked in, then the section: "Hotel Lipa / Znamka". -->
            <p class="flex items-center gap-1.5 min-w-0 text-sm">
                <template v-if="tenantName">
                    <span class="truncate text-muted">{{ tenantName }}</span>
                    <UIcon name="i-lucide-chevron-right" class="size-4 shrink-0 text-dimmed" />
                </template>
                <span class="font-semibold text-highlighted truncate">{{ $t(titleKey) }}</span>
            </p>
        </div>

        <div class="flex items-center shrink-0 gap-1">
            <!-- Milo (docs/API.md §14): the still head as the button — no second 3D figure up
                 here — hidden while the drawer is open, as two Milos read as two assistants. -->
            <UButton
                v-if="milo.offered && !milo.open"
                color="neutral"
                variant="ghost"
                class="p-1"
                :aria-label="$t('milo.open')"
                :title="$t('milo.open')"
                @click="milo.show()"
            >
                <img :src="miloPose('idle')" alt="" class="size-7 rounded-full bg-elevated object-cover" draggable="false">
            </UButton>
            <ColorModeToggle />
        </div>
        <MiloDrawer />
    </header>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useRoute } from 'vue-router'

    import MiloDrawer from '@/components/milo/MiloDrawer.vue'
    import ColorModeToggle from '@/layouts/components/ColorModeToggle.vue'
    import { navTitleKey, useLayout } from '@/layouts/layout'
    import { miloPose } from '@/libs/milo/milo'
    import { useMiloStore } from '@/stores/milo'
    import { useTenantStore } from '@/stores/tenant'

    const route       = useRoute()
    const tenantStore = useTenantStore()
    const milo        = useMiloStore()
    const { isMobile, openMenu } = useLayout()

    const titleKey = computed((): string => navTitleKey(String(route.name ?? '')))

    /** Only on a tenant's page, and only once that tenant is the one loaded. */
    const tenantName = computed((): string => route.meta.tenant && tenantStore.tenant?.id === route.params.tid
        ? tenantStore.tenant.name
        : '')
</script>
